import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import inovaImage from "../../../assets/inova@2x.png";

/* =========================
   ESTILOS GLOBAIS DO PDF
========================= */

export const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontSize: 9,
    backgroundColor: "#ffffff",
    color: "#111111",
  },

  /* =========================
     HEADER
  ========================= */

  headerTopBar: {
    height: 1.5,
    backgroundColor: "#0F73B2",
    marginBottom: 12,
  },

  header: {
    position: "relative",
    height: 48,
    marginBottom: 8,
    justifyContent: "center",
  },

  logo: {
    position: "absolute",
    left: 0,
    top: 7,
    width: 58,
    height: 27,
  },

  titleContainer: {
    width: "100%",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingRight: 2,
  },

  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0F73B2",
    textAlign: "right",
  },

  subtitle: {
    fontSize: 8,
    color: "#7A858E",
    marginTop: 3,
    textAlign: "right",
  },

  /* =========================
     INFORMAÇÕES
  ========================= */

  infoSection: {
    marginTop: 5,
    marginBottom: 10,
  },

  infoTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1E2933",
    marginBottom: 3,
  },

  infoSubtitle: {
    fontSize: 8,
    color: "#7A858E",
  },

  /* =========================
     TABELA
  ========================= */
  tableContainer: {
    width: "100%",
    borderWidth: 0.7,
    borderColor: "#D9E2EA",
    borderRadius: 3,
    overflow: "hidden",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0B6FA8",
    paddingVertical: 7,
    paddingHorizontal: 9,
    fontSize: 8,
    fontWeight: "bold",
    color: "#FFFFFF",
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 28,
    paddingVertical: 6,
    paddingHorizontal: 9,
    alignItems: "center",
    borderBottomWidth: 0.5,
    borderBottomColor: "#E3E8ED",
  },

  zebraRow: {
    backgroundColor: "#F8FAFC",
  },

  cell: {
    flex: 1,
    fontSize: 8.5,
    color: "#26323D",
  },

  /* =========================
     FOOTER
  ========================= */

  footer: {
    position: "absolute",
    bottom: 8,
    left: 25,
    right: 25,
    textAlign: "center",
    fontSize: 8,
    color: "#8B8D98",
  },
});

/* =========================
   COMPONENTE BASE
========================= */

function BasePDF({
  title = "Relatório",
  columns = [],
  rows = [],
  columnStyles = [],
  orientation = "landscape",
}) {
  const dataAtual = new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <Document>
      <Page
        size="A4"
        orientation={orientation}
        style={styles.page}
      >
        {/* =========================
        HEADER
    ========================= */}

        <View>
          <View style={styles.headerTopBar} />

          <View style={styles.header}>
            <Image
              style={styles.logo}
              src={inovaImage}
            />

            <View style={styles.titleContainer}>
              <Text style={styles.title}>
                {title}
              </Text>

              <Text style={styles.subtitle}>
                Documento gerado em {dataAtual}
              </Text>
            </View>
          </View>
        </View>

        {/* =========================
        RESUMO
    ========================= */}

        <View style={styles.infoSection}>
          <Text style={styles.infoTitle}>
            ENFERMEIROS
          </Text>

          <Text style={styles.infoSubtitle}>
            {rows.length} registro
            {rows.length !== 1 ? "s" : ""} encontrado
            {rows.length !== 1 ? "s" : ""}
          </Text>
        </View>

        {/* =========================
        TABELA
    ========================= */}

        <View style={styles.tableContainer}>

          {/* CABEÇALHO */}

          <View style={styles.tableHeader}>
            {columns.map((col, index) => (
              <Text
                key={index}
                style={
                  columnStyles[index]
                    ? {
                      ...columnStyles[index],
                      fontSize: 8,
                      fontWeight: "bold",
                      color: "#FFFFFF",
                    }
                    : {
                      ...styles.cell,
                      fontSize: 8,
                      fontWeight: "bold",
                      color: "#FFFFFF",
                    }
                }
              >
                {col}
              </Text>
            ))}
          </View>

          {/* LINHAS */}

          {rows.map((row, rowIndex) => (
            <View
              key={rowIndex}
              style={[
                styles.tableRow,
                rowIndex % 2 === 0 && styles.zebraRow,
              ]}
            >
              {row.map((cell, cellIndex) => (
                <Text
                  key={cellIndex}
                  style={
                    columnStyles[cellIndex]
                      ? {
                        ...columnStyles[cellIndex],
                        fontSize: 8.5,
                        color: "#26323D",
                      }
                      : {
                        ...styles.cell,
                        fontSize: 8.5,
                        color: "#26323D",
                      }
                  }
                >
                  {cell ?? "-"}
                </Text>
              ))}
            </View>
          ))}

        </View>

        {/* =========================
        FOOTER
    ========================= */}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />

      </Page>
    </Document>
  );
}

/* ==================================================
   RELATÓRIO DE ENFERMEIROS
================================================== */

export function EnfermeirosDocument({ marc = [] }) {
  const columns = [
    "Nome",
    "CPF",
    "NOME FANTASIA",
    "MULTIPLOS ACESSOS",
  ];

  const formatarCPF = (cpf) => {
    if (!cpf) return "-";

    const valor = String(cpf).replace(/\D/g, "");

    if (valor.length !== 11) {
      return cpf;
    }

    return valor.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      "$1.$2.$3-$4"
    );
  };

  const rows = marc.map((m) => [
    m.nome || "-",
    formatarCPF(m.cpf),
    m.razao_social || "-",
    m.multiple_checks === true ? "Sim" : "Não",
  ]);

  const columnStyles = [
    {
      width: "30%",
    },
    {
      width: "20%",
      textAlign: "center",
    },
    {
      width: "30%",
    },
    {
      width: "20%",
      textAlign: "center",
    },
  ];

  return (
    <BasePDF
      title="Relatório de Enfermeiros"
      columns={columns}
      rows={rows}
      columnStyles={columnStyles}
    />
  );
}

export default BasePDF;